import { css } from "@emotion/react";
import styled from "@emotion/styled";
import { FC, useEffect, useState } from "react";
import { useSelector } from "react-redux";

import { Add, HelpDeskColored, KnowledgeBaseColored } from "@livechat/design-system-icons";
import {
  Button,
  Heading,
  Icon,
  Modal,
  ModalHeader,
  RadiusToken,
  ShadowToken,
  SpacingToken,
  Text,
} from "@livechat/design-system-react-components";

import Flex from "components/layout/Flex";
import { useKnowledgeBaseIntroFlags } from "hooks/use-knowledgebase-intro-flags";
import { useUpdateKnowledgebaseIntroFlags } from "hooks/use-update-knowledgebase-intro-flags";
import { selectIsViewer } from "store/entities/agents";
import { selectIsKbWidgetPreinstalled } from "store/entities/license";
import { withVar } from "themes/helpers";

export const KbWidgetPreinstalledModal: FC = () => {
  const isKbWidgetPreinstalled = useSelector(selectIsKbWidgetPreinstalled);
  const isViewer = useSelector(selectIsViewer);

  if (!isKbWidgetPreinstalled || isViewer) return null;

  const { introFlags, isLoading } = useKnowledgeBaseIntroFlags();
  const { mutate } = useUpdateKnowledgebaseIntroFlags();

  const [isModalVisible, setIsModalVisible] = useState(false);
  const shouldDisplayModal = !isLoading && introFlags && !introFlags.helpDeskIntegrationModalSeen;

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const setModalAsSeen = () => {
    setIsModalVisible(false);
    mutate(["helpDeskIntegrationModalSeen"]);
  };

  useEffect(() => {
    if (shouldDisplayModal) {
      setIsModalVisible(true);
    }
  }, [shouldDisplayModal]);

  if (!isModalVisible) return null;

  return (
    <Modal heading={<ModalHeader />} onClose={setModalAsSeen}>
      <Flex
        gap={withVar(SpacingToken.Spacing4)}
        column
        alignItems="center"
        paddingBottom={withVar(SpacingToken.Spacing10)}
        paddingH={withVar(SpacingToken.Spacing9)}
      >
        <Flex
          gap={withVar(SpacingToken.Spacing3)}
          column
          alignItems="center"
          maxWidth="352px"
          paddingBottom={withVar(SpacingToken.Spacing6)}
          css={css`
            text-align: center;
          `}
        >
          <Flex gap={withVar(SpacingToken.Spacing2)} paddingBottom={withVar(SpacingToken.Spacing1)}>
            <Logo>
              <Icon source={HelpDeskColored} size="large" />
            </Logo>
            <Icon source={Add} />
            <Logo>
              <Icon source={KnowledgeBaseColored} size="large" />
            </Logo>
          </Flex>
          <Heading css={{ fontWeight: 600 }}>Access KnowledgeBase directly in HelpDesk</Heading>
          <Text>
            Use help center articles to proactively resolve tickets
            <br /> and guide customers with ever-present information.
          </Text>
        </Flex>
        <Flex gap={withVar(SpacingToken.Spacing2)}>
          <Button size="large" onClick={setModalAsSeen} kind="secondary">
            Got it
          </Button>
          <Button
            size="large"
            onClick={setModalAsSeen}
            kind="primary"
            href="https://www.helpdesk.com/help/integrate-helpdesk-with-knowledgebase/"
            target="_blank"
          >
            See how it works
          </Button>
        </Flex>
      </Flex>
    </Modal>
  );
};

const Logo = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;

  box-shadow: ${withVar(ShadowToken.Float)};
  border-radius: ${withVar(RadiusToken.Radius3)};
  width: 40px;
  height: 40px;
`;
